'use client';

import {useVideoPlayer, useVideoPlayerState} from '@components/@ui/VideoPlayer';
import {BusEventEnum, EventBus} from '@core/eventBus';
import {Slider} from '@mui/material';
import {IChapterCue} from '@utils/parseChapterMarks';
import {motion} from 'framer-motion';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useActiveChapterIndex} from '../@hooks/useActiveChapterIndex';
import {useChapterHoverTracker} from '../@hooks/useChapterHoverTracker';
import styles from './ChaptersProgressBar.module.css';
import ChaptersProgressBarSegments from './Segments/ChaptersProgressBarSegments';
import ChaptersProgressBarThumb from './Thumb/ChaptersProgressBarThumb';
import ChaptersProgressBarTooltip from './Tooltip/ChaptersProgressBarTooltip';

interface Props {
    cues: IChapterCue[];
}

const EASE_IN_OUT: [number, number, number, number] = [0.4, 0, 0.2, 1];

export const CHAPTER_PROGRESS_TRANSITION = {duration: 0.2, ease: EASE_IN_OUT};

const ChaptersProgressBar: FC<Props> = ({cues}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const duration = useVideoPlayerState(s => s.duration);
    const currentTime = useVideoPlayerState(s => s.currentTime);
    const isReady = useVideoPlayerState(s => s.isReady);
    const activeCueIndex = useActiveChapterIndex(cues);

    const {_injectIntoVideoJsUI, seek, _player} = useVideoPlayer();

    const {
        ref: wrapperRef,
        cursorX: hoverX,
        hoveredTime,
        hoveredChapterIndex,
        handleMouseMove,
        handleMouseLeave,
        setHoverByTime,
    } = useChapterHoverTracker(cues, duration);

    useEffect(() => {
        const sub = EventBus.on(BusEventEnum.USER_INTERACTION, () => {
            setHasUserInteracted(true);
        });
        return () => {
            sub.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (!isReady || !duration || !cues.length) {
            return;
        }

        const el = document.createElement('div');
        el.className = styles.injectedContainer;

        const controlBar = _player.el().querySelector('.vjs-control-bar');
        const nativeChaptersBtn = controlBar?.querySelector(
            '.vjs-chapters-button',
        );
        nativeChaptersBtn?.remove();

        const nativeProgressControls = _player
            .el()
            .querySelector('.vjs-progress-control') as HTMLElement;

        if (nativeProgressControls) {
            nativeProgressControls.style.display = 'none';
        }

        _injectIntoVideoJsUI('.vjs-control-bar', el);
        containerRef.current = el;

        return () => {
            el.remove();
            containerRef.current = null;

            if (nativeProgressControls) {
                nativeProgressControls.style.display = '';
            }
        };
    }, [isReady, duration, cues]);

    const isThumbHovered = useMemo(
        () => hoveredChapterIndex === activeCueIndex,
        [hoveredChapterIndex, activeCueIndex],
    );

    const shouldSliderScale = hasUserInteracted || hoverX !== null;

    if (!isReady || !duration || !cues.length) {
        return null;
    }

    if (!containerRef.current) {
        return null;
    }
    return createPortal(
        <div
            ref={wrapperRef}
            className={styles.wrapper}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <ChaptersProgressBarSegments
                cues={cues}
                duration={duration}
                currentTime={currentTime}
                hoveredTime={hoveredTime}
                hoveredChapterIndex={hoveredChapterIndex}
                isUserInteracting={hasUserInteracted}
            />

            <ChaptersProgressBarThumb
                currentTime={currentTime}
                duration={duration}
                isHovered={isThumbHovered}
                isUserInteracting={hasUserInteracted}
            />

            <ChaptersProgressBarTooltip
                cues={cues}
                hoveredTime={hoveredTime}
                hoverX={hoverX}
                wrapperRef={wrapperRef}
            />

            <motion.div
                className={styles.pseudoSliderWrapper}
                animate={{scaleY: shouldSliderScale ? 1.5 : 1}}
                transition={CHAPTER_PROGRESS_TRANSITION}
            >
                <Slider
                    size="small"
                    min={0}
                    step={0.1}
                    max={duration}
                    value={currentTime}
                    onChange={(_, value) => {
                        const newValue = Array.isArray(value)
                            ? value[0]
                            : value;
                        setHasUserInteracted(true);
                        setHoverByTime(newValue);
                        seek(newValue);
                    }}
                    onChangeCommitted={() => {
                        setTimeout(() => {
                            setHasUserInteracted(false);
                        }, 100);
                    }}
                    className={styles.slider}
                    valueLabelDisplay="off"
                />
            </motion.div>
        </div>,
        containerRef.current,
    );
};

export default ChaptersProgressBar;
